import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { cn } from 'utils/classnames';
import { scrollToPosition } from 'utils/scroll-to-position';
import { useIsomorphicLayoutEffect } from 'common/hooks';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { getTitle } from 'features/page-model/ducks/get-model';
import { overscroll, calcChildFromProps, onCarouselScroll } from 'utils/carousel-utils';
import { CarouselProps, OnControlClickProps } from './carousel.types';
import { wrapperStyles, Container } from './carousel.styles';
import { CarouselControl } from '../carousel-control';
import { analyticsUtils } from '../../../../utils';

export const Carousel: React.FC<CarouselProps> = ({
  children,
  className,
  fullScroll,
  testMock,
  controlSettings,
  autoplay,
  onChange,
  hideControls,
  ...props
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [inScrollNow, setInScrollNow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [currentChild, setCurrentChild] = useState(0);
  const { smMobile, mobile, tablet } = useBreakpoints() ?? {};
  const title = useSelector(getTitle);
  const gap = 20;
  const servicesInStore = t('servicesInStore');

  const onControlClick = useCallback(
    (clickProps: OnControlClickProps = {}) => {
      let { cancelAnimations } = clickProps;
      const element = containerRef.current;
      const carouselGap = gap;
      if ((clickProps.direction && inScrollNow) || !element) return;
      if (element) {
        const { offset } = overscroll({
          element,
          currentChild,
          setCurrentChild,
          gap: carouselGap,
          onChange,
          fullScroll,
          setInScrollNow,
          direction: clickProps.direction,
        });
        if (onChange) onChange(offset, clickProps.direction);
        const scrollSpeed = cancelAnimations ? { maxDuration: 0, minDuration: 0 } : { maxDuration: 300 };
        scrollToPosition({ x: offset, y: 0 }, { ...scrollSpeed, elementToScroll: element }).then(() => {
          setInScrollNow(false);
          setCurrentChild(
            calcChildFromProps({
              direction: clickProps.direction,
              currentChild,
              length: element.children.length,
              fullScroll,
            }),
          );
        });

        analyticsUtils.sendEvent('GA:clickButton', {
          elementType: 'button',
          elementBlock: 'shopPage', // блок, в котором расположен элемент
          elementName: `${title} / ${servicesInStore}`, // название блока
          elementText: clickProps.direction === 'right' ? 'switch-forward' : 'switch-backward', // заголовок карточки (switch-forward или switch-backward для стрелочек)
        });
      }
    },
    [inScrollNow, currentChild, onChange, fullScroll, title, gap, servicesInStore],
  );
  const checkCanScroll = useCallback(() => {
    const element = containerRef.current;
    if (element) {
      const { scrollLeft, offsetWidth, scrollWidth } = element;
      const indentation = 50;
      if (scrollLeft < indentation) {
        setCanScrollLeft(false);
      } else {
        setCanScrollLeft(true);
      }
      if (scrollWidth - offsetWidth - indentation < scrollLeft) {
        setCanScrollRight(false);
      } else {
        setCanScrollRight(true);
      }
    }
  }, []);
  useIsomorphicLayoutEffect(() => {
    checkCanScroll();
  }, [checkCanScroll]);

  const scrollProps = {
    onChangeFn: onChange,
    scrollState: inScrollNow,
    checkScroll: checkCanScroll,
  };
  return (
    <div className={cn(className, wrapperStyles)} {...props}>
      <>
        {!(smMobile || mobile || tablet) && canScrollLeft && controlSettings && !hideControls && (
          <CarouselControl
            {...controlSettings}
            label={t('ui.prevSlide')}
            direction="left"
            data-qa="carousel-test-left"
            onClick={() => onControlClick({ direction: 'left' })}
            data-qa-left-arrow=""
          />
        )}
        <Container
          style={{ '--grid-gap': `${gap}px` }}
          ref={containerRef}
          onScroll={onCarouselScroll(scrollProps)}
          inScrollNow={inScrollNow}
          data-qa-carousel=""
        >
          {children}
        </Container>
        {!(smMobile || mobile || tablet) && canScrollRight && controlSettings && !hideControls && (
          <CarouselControl
            {...controlSettings}
            label={t('ui.nextSlide')}
            direction="right"
            data-qa="carousel-test-right"
            onClick={() => onControlClick({ direction: 'right' })}
            data-qa-right-arrow=""
          />
        )}
      </>
    </div>
  );
};
